import React, { useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "./ui/dialog";
import {
  Sparkles, Loader2, Flame, Thermometer, Snowflake, Target,
  MessageSquare, AlertTriangle, CheckCircle2, TrendingUp,
} from "lucide-react";
import { toast } from "sonner";

const TEMP_CONFIG = {
  quente: { label: "Quente", color: "#DC2626", bg: "#FEE2E2", icon: Flame, ring: "ring-red-300" },
  morno: { label: "Morno", color: "#F97316", bg: "#FED7AA", icon: Thermometer, ring: "ring-orange-300" },
  frio: { label: "Frio", color: "#0284C7", bg: "#DBEAFE", icon: Snowflake, ring: "ring-blue-300" },
};

export default function IAEstrategicaDialog({ leadId, leadNome }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resultado, setResultado] = useState(null);

  const analisar = async () => {
    setLoading(true);
    setResultado(null);
    try {
      const { data } = await api.post("/ia-estrategica/analisar", { lead_id: leadId });
      setResultado(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao analisar com IA");
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (v) => {
    setOpen(v);
    if (v && !resultado) {
      analisar();
    }
  };

  const temp = resultado?.temperatura;
  const tempCfg = TEMP_CONFIG[temp] || TEMP_CONFIG.morno;
  const TempIcon = tempCfg.icon;

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogTrigger asChild>
        <Button
          data-testid="ia-estrategica-btn"
          size="sm"
          className="text-white"
          style={{ background: "linear-gradient(90deg, #7C3AED, #4F46E5)" }}
        >
          <Sparkles size={14} className="mr-1" /> Analisar com IA
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles size={20} style={{ color: "#7C3AED" }} />
            IA Estratégica — {leadNome}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-20 text-center">
            <Loader2 size={40} className="animate-spin mx-auto mb-3" style={{ color: "#7C3AED" }} />
            <div className="font-medium" style={{ color: "#1B3A6B" }}>
              Analisando histórico do negócio...
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              Coletando propostas, opens, comunicações e gerando direcionamento estratégico.
            </div>
          </div>
        ) : resultado ? (
          <div className="space-y-4">
            {/* Header: Temperatura + Probabilidade */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Card
                className={`p-4 ring-2 ${tempCfg.ring}`}
                style={{ background: tempCfg.bg }}
                data-testid="ia-temperatura"
              >
                <div className="flex items-center gap-2 mb-1.5">
                  <TempIcon size={20} style={{ color: tempCfg.color }} />
                  <div className="text-xs uppercase tracking-wide font-bold" style={{ color: tempCfg.color }}>
                    Temperatura
                  </div>
                </div>
                <div className="text-2xl font-display font-bold" style={{ color: tempCfg.color }}>
                  {tempCfg.label}
                </div>
                {resultado.temperatura_motivo && (
                  <div className="text-xs mt-1" style={{ color: tempCfg.color, opacity: 0.85 }}>
                    {resultado.temperatura_motivo}
                  </div>
                )}
              </Card>
              <Card className="p-4 bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200" data-testid="ia-probabilidade">
                <div className="flex items-center gap-2 mb-1.5">
                  <TrendingUp size={20} className="text-emerald-700" />
                  <div className="text-xs uppercase tracking-wide font-bold text-emerald-700">
                    Probabilidade de fechamento
                  </div>
                </div>
                <div className="text-3xl font-display font-bold text-emerald-700">
                  {resultado.probabilidade_fechamento}%
                </div>
                <div className="w-full bg-emerald-200/60 rounded-full h-1.5 mt-2 overflow-hidden">
                  <div
                    className="h-full bg-emerald-600 transition-all duration-1000"
                    style={{ width: `${resultado.probabilidade_fechamento}%` }}
                  />
                </div>
              </Card>
            </div>

            {/* Diagnóstico */}
            <Card className="p-4 border-l-4" style={{ borderLeftColor: "#1B3A6B" }} data-testid="ia-diagnostico">
              <div className="flex items-center gap-2 mb-2">
                <Target size={16} style={{ color: "#1B3A6B" }} />
                <div className="text-xs uppercase tracking-wide font-bold" style={{ color: "#1B3A6B" }}>
                  Diagnóstico
                </div>
              </div>
              <p className="text-sm leading-relaxed">{resultado.diagnostico}</p>
            </Card>

            {/* Observação crítica */}
            {resultado.observacao_critica && (
              <Card className="p-4 bg-amber-50 border-amber-200" data-testid="ia-obs-critica">
                <div className="flex items-start gap-2">
                  <AlertTriangle size={18} className="text-amber-700 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-xs uppercase tracking-wide font-bold text-amber-700 mb-1">
                      Observação crítica
                    </div>
                    <p className="text-sm leading-relaxed text-amber-900">
                      {resultado.observacao_critica}
                    </p>
                  </div>
                </div>
              </Card>
            )}

            {/* Ações priorizadas */}
            {resultado.acoes?.length > 0 && (
              <Card className="p-4" data-testid="ia-acoes">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle2 size={16} style={{ color: "#16A34A" }} />
                  <div className="text-xs uppercase tracking-wide font-bold" style={{ color: "#16A34A" }}>
                    Ações priorizadas
                  </div>
                  <Badge variant="outline" className="text-xs ml-auto">{resultado.acoes.length}</Badge>
                </div>
                <ol className="space-y-2 list-none">
                  {resultado.acoes.map((acao, i) => (
                    <li key={i} className="flex gap-3" data-testid={`ia-acao-${i}`}>
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-bold">
                        {i + 1}
                      </span>
                      <span className="text-sm leading-relaxed">{acao}</span>
                    </li>
                  ))}
                </ol>
              </Card>
            )}

            {/* Script sugerido */}
            {resultado.script_sugerido && (
              <Card className="p-4 bg-slate-900 text-slate-50 border-slate-700" data-testid="ia-script">
                <div className="flex items-center gap-2 mb-3">
                  <MessageSquare size={16} style={{ color: "#F97316" }} />
                  <div className="text-xs uppercase tracking-wide font-bold" style={{ color: "#F97316" }}>
                    Script sugerido para próxima abordagem
                  </div>
                </div>
                <p className="text-sm leading-relaxed italic font-display">
                  "{resultado.script_sugerido}"
                </p>
              </Card>
            )}

            {/* Footer com metadata */}
            <div className="flex justify-between items-center text-xs text-muted-foreground border-t pt-3">
              <span>
                Gerado por {resultado.gerado_por_nome} · Modelo: Claude Sonnet 4.5
              </span>
              <Button size="sm" variant="outline" onClick={analisar} disabled={loading}>
                <Sparkles size={12} className="mr-1" /> Re-analisar
              </Button>
            </div>
          </div>
        ) : (
          <div className="py-12 text-center text-muted-foreground">
            Falha ao gerar análise. Tente novamente.
            <div className="mt-3">
              <Button size="sm" onClick={analisar}>Tentar novamente</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
